import React, { useMemo, useCallback, useState, useRef } from 'react';
import { AnalyticsListener, type UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Shortcuts from '@atlassian/jira-common-components-keyboard-shortcuts/src/shortcuts/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { FilterButton } from '@atlassian/jira-issue-navigator-actions-common/src/ui/filter-button/index.tsx';
import { isCustomFilter } from '@atlassian/jira-issue-navigator-actions-common/src/utils/filters/index.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useSubProduct } from '@atlassian/jira-subproduct/src/index.tsx';
import ThirdPartyNudge from '@atlassian/jira-third-party-nudge/src/ui/async.tsx';
import type {
	CustomTriggerButtonProps,
	RenderCustomTriggerButton,
} from '@atlassian/jira-share-button/src/common/types.tsx';
import JiraShareButton from '@atlassian/jira-share-button/src/index.tsx';
import { INTEGRATION_TYPES } from '../common/constants.tsx';
import { shareSearchClientConfig } from '../common/utils/share-search-client.tsx';
import { getShareSubProductId } from '../common/utils/sub-product.tsx';
import { messages } from './messages.tsx';

const getShareLink = (param: string, value: string) => {
	// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
	const { hostname, protocol } = window.location;
	return `${protocol}//${hostname}/issues/?${param}=${encodeURIComponent(value)}`;
};

type Props = {
	jql: string;
	filterId?: string;
	isModifiedFilter: boolean;
};

const CustomTriggerButton: RenderCustomTriggerButton = (
	{ isDisabled, isSelected, onClick }: CustomTriggerButtonProps,
	triggerProps,
) => {
	const { formatMessage } = useIntl();
	const buttonRef = useRef<HTMLElement>(null);

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const onKeyboardShortcutClick = useCallback(() => {
		fireUIAnalytics(
			createAnalyticsEvent({
				action: 'pressed',
				actionSubject: 'keyboardShortcut',
			}),
			'shareSearchCriteriaModalKeyboardShortcut',
			{
				keyPressed: 's',
				keyboardShortcut: true,
			},
		);

		// Simulate a real button click because the Entrypoint doesn't use an onClick callback
		buttonRef.current?.click();
	}, [createAnalyticsEvent]);

	const shortcutsKeyMap = useMemo(
		() => ({
			s: {
				callback: onKeyboardShortcutClick,
				label: <div>{formatMessage(messages.shareKeyboardShortcut)}</div>,
			},
		}),
		[formatMessage, onKeyboardShortcutClick],
	);

	return (
		<>
			<Shortcuts keyMap={shortcutsKeyMap} />
			<FilterButton
				ref={buttonRef}
				isDisabled={isDisabled}
				isSelected={isSelected}
				onClick={onClick}
				label=""
				text={formatMessage(messages.share)}
				customTriggerProps={{ ...triggerProps, 'aria-haspopup': false }}
				vcId="share-button"
			/>
		</>
	);
};

const addContextToEvent = (event: UIAnalyticsEvent) => {
	const attributes = event.context.map((context) => context.attributes);
	event.update((payload: UIAnalyticsEvent) => ({
		...payload,
		attributes: Object.assign(event.payload.attributes || {}, ...attributes),
	}));
};

export const ShareButton = ({ jql, filterId, isModifiedFilter }: Props): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const subProduct = useSubProduct();

	const [shouldShowThirdPartyNudge, setShouldShowThirdPartyNudge] = useState(false);

	const showFilterShareLinkNew =
		filterId !== undefined && isCustomFilter(filterId) && !isModifiedFilter;

	// share custom filters by ID if the JQL is not overridden, otherwise just share the JQL
	const additionalProps = showFilterShareLinkNew
		? {
				integrationType: INTEGRATION_TYPES.FILTER,
				shareContentType: INTEGRATION_TYPES.FILTER,
				shareLink: getShareLink('filter', filterId),
				shareTitle: formatMessage(messages.filter),
				shareFormTitle: formatMessage(messages.dialogTitleFilter),
			}
		: {
				integrationType: INTEGRATION_TYPES.SEARCH,
				shareContentType: INTEGRATION_TYPES.SEARCH,
				shareLink: getShareLink('jql', jql),
				shareTitle: formatMessage(messages.search),
				shareFormTitle: formatMessage(messages.dialogTitleSearch),
			};

	return (
		<AnalyticsListener channel="*" onEvent={addContextToEvent}>
			{shouldShowThirdPartyNudge && <ThirdPartyNudge />}
			<JiraShareButton
				teamName="empanada"
				errorBoundaryId="issue-navigator-action-share"
				triggerButtonStyle="icon-with-text"
				productId="jira"
				subProductId={getShareSubProductId(subProduct)}
				objectAri="" // The legacy sharing API does not use ARIs
				legacyShareClientConfig={shareSearchClientConfig}
				renderCustomTriggerButton={CustomTriggerButton}
				onTriggerButtonClick={() => {
					setShouldShowThirdPartyNudge(true);
				}}
				dialogPlacement="bottom-end"
				isAutoOpenDialog
				{...additionalProps}
			/>
		</AnalyticsListener>
	);
};
